import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiFetch, readJsonResponse } from '../api/client';
import {
  compareFirmwareVersionsNewestFirst,
  formatFirmwareVersionLabel,
  isAllRisksView,
} from '../pages/firmwareQuery';
import { loadAllRisksHistoryPayload } from '../pages/firmwareAllRisksHistory';

const HISTORY_RANGE_LABELS = {
  month: 'месяц',
  year: 'год',
  all: 'весь период',
};

/** Same green → yellow → orange → red scale as table % column. */
export function riskPercentColor(percent) {
  const value = Math.min(100, Math.max(0, Number(percent) || 0));
  if (value <= 0) {
    return 'hsl(142 55% 36%)';
  }

  const t = value / 100;
  const hue = Math.round(142 * (1 - t));
  const saturation = Math.round(62 + 28 * t);
  const lightness = Math.round(38 - 6 * t);
  return `hsl(${hue} ${saturation}% ${lightness}%)`;
}

function formatPct(value) {
  const number = Math.round(Number(value) * 100) / 100;
  return Number.isFinite(number) ? `${number}%` : '—';
}

/** Classic tooltip — LK ships recharts 2.x (no useYAxisScale / usePlotArea). */
function HistoryTooltip({ active, payload, label }) {
  if (!active || !payload?.length) {
    return null;
  }

  const points = payload
    .filter((item) => item?.value != null && Number.isFinite(Number(item.value)))
    .map((item) => ({
      key: String(item.dataKey),
      name: formatFirmwareVersionLabel(item.name),
      value: Number(item.value),
      color: item.color,
    }))
    .sort((left, right) => right.value - left.value);

  if (points.length === 0) {
    return null;
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white/95 px-2 py-1.5 text-[11px] shadow-sm dark:border-slate-700 dark:bg-slate-950/95">
      <div className="mb-1 font-medium text-slate-600 dark:text-slate-300">Снимок {label}</div>
      {points.map((point) => (
        <div key={point.key} className="whitespace-nowrap leading-4" style={{ color: point.color }}>
          {point.name} : {formatPct(point.value)}
        </div>
      ))}
    </div>
  );
}

function formatSnapshotLabel(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function buildEmptyAxisDays(days = 7) {
  const result = [];
  const now = new Date();
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date(now);
    date.setDate(now.getDate() - offset);
    const iso = date.toISOString().slice(0, 10);
    result.push({ date: iso, label: formatSnapshotLabel(iso) });
  }
  return result;
}

function EmptyHistoryChart({ message }) {
  const emptyData = useMemo(() => buildEmptyAxisDays(7), []);

  return (
    <div className="relative h-full min-h-[200px]">
      <ResponsiveContainer width="100%" height="100%" minHeight={200}>
        <LineChart data={emptyData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" />
          <YAxis
            type="number"
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tick={{ fontSize: 10 }}
            stroke="#94a3b8"
            tickFormatter={(value) => `${value}%`}
            width={44}
            allowDataOverflow
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4">
        <p className="rounded-lg bg-white/90 px-3 py-2 text-center text-xs text-slate-500 shadow-sm dark:bg-slate-950/85 dark:text-slate-300">
          {message}
        </p>
      </div>
    </div>
  );
}

/**
 * At-risk % lines per firmware version (snapshots every 6 hours).
 * Line color = latest % (same scale as table). Legend toggles series.
 */
export default function FirmwareRiskHistoryChart({
  organizationId,
  connectionType,
  label,
  hideTest = true,
  riskNumber = '1',
  historyRange = 'month',
  /** When provided by StatusPage, skip a second /stats/history fetch. */
  historyPayload = null,
}) {
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [enabledKeys, setEnabledKeys] = useState(() => new Set());

  useEffect(() => {
    if (historyPayload) {
      const series = historyPayload?.series || [];
      const dates = historyPayload?.dates || [];
      setPayload(historyPayload);
      setHasHistory(dates.length > 0 && series.length > 0);
      setIsLoading(false);
      return undefined;
    }

    if (!organizationId || !connectionType) {
      setPayload(null);
      setHasHistory(false);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      setIsLoading(true);

      try {
        let body = null;
        if (isAllRisksView(riskNumber)) {
          body = await loadAllRisksHistoryPayload({
            organizationId,
            connectionType,
            historyRange,
            hideTest,
          });
        } else {
          const query = new URLSearchParams({
            organization_id: String(organizationId),
            connection_type: connectionType,
            range: historyRange,
            hide_test: hideTest ? '1' : '0',
            risk_number: String(riskNumber),
          });
          const response = await apiFetch(`/api/firmware/stats/history?${query}`);
          body = await readJsonResponse(response, 'Firmware history');
          if (!response.ok) {
            body = null;
          }
        }

        if (cancelled) {
          return;
        }

        if (!body) {
          setPayload(null);
          setHasHistory(false);
          return;
        }

        const series = body?.series || [];
        const dates = body?.dates || [];
        setPayload(body);
        setHasHistory(dates.length > 0 && series.length > 0);
      } catch {
        if (!cancelled) {
          setPayload(null);
          setHasHistory(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [historyPayload, organizationId, connectionType, hideTest, riskNumber, historyRange]);

  const { chartData, seriesMeta } = useMemo(() => {
    const series = payload?.series || [];
    const dates = payload?.dates || [];

    if (!dates.length || !series.length) {
      return { chartData: [], seriesMeta: [] };
    }

    const allRisks = isAllRisksView(payload?.risk_number) || isAllRisksView(riskNumber);
    const ranked = allRisks
      ? series
      : [...series]
          .sort((left, right) =>
            compareFirmwareVersionsNewestFirst(left.firmware_version, right.firmware_version),
          )
          .slice(0, 12);

    const byVersion = new Map(
      ranked.map((item) => [
        item.firmware_version,
        new Map(
          (item.points || []).map((point) => [
            point.snapshot_at || point.snapshot_date,
            point.at_risk_pct,
          ]),
        ),
      ]),
    );

    const seriesMeta = ranked.map((item) => {
      const latest = item.points?.[item.points.length - 1];
      const latestPct = Number(latest?.at_risk_pct) || 0;
      const fixedColor = payload?.series_colors?.[item.firmware_version];
      return {
        key: item.firmware_version,
        label: allRisks ? item.firmware_version : formatFirmwareVersionLabel(item.firmware_version),
        latestPct,
        color: fixedColor || riskPercentColor(latestPct),
      };
    });

    const chartData = dates.map((date) => {
      const row = { date, label: formatSnapshotLabel(date) };
      for (const item of seriesMeta) {
        const pct = byVersion.get(item.key)?.get(date);
        row[item.key] = pct == null ? null : Number(pct);
      }
      return row;
    });

    return { chartData, seriesMeta };
  }, [payload, riskNumber]);

  useEffect(() => {
    setEnabledKeys(new Set(seriesMeta.map((item) => item.key)));
  }, [seriesMeta]);

  const visibleSeries = useMemo(
    () => seriesMeta.filter((item) => enabledKeys.has(item.key)),
    [seriesMeta, enabledKeys],
  );

  function toggleSeries(key) {
    setEnabledKeys((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        if (next.size <= 1) {
          return previous;
        }
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  return (
    <div className="flex h-full min-h-[12.5rem] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950/70 sm:min-h-[14rem]">
      <div className="border-b border-slate-200 px-3 py-2 dark:border-slate-800">
        <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">Динамика % в рисках</p>
        <p className="text-[11px] text-slate-500">
          {label}
          {isAllRisksView(riskNumber) ? ' · все риски' : ''} · каждые 6 часов ·{' '}
          {HISTORY_RANGE_LABELS[historyRange] || 'месяц'}
        </p>
      </div>

      <div className="min-h-0 flex-1 px-1 py-2">
        {isLoading && !hasHistory ? (
          <div className="flex h-full min-h-[180px] items-center justify-center text-xs text-slate-400 sm:min-h-[200px]">
            Загрузка…
          </div>
        ) : !hasHistory || chartData.length === 0 ? (
          <EmptyHistoryChart message="Пока нет данных для графика. Снимки создаются каждые 6 часов." />
        ) : (
          <div className="flex h-full min-h-[180px] flex-col sm:min-h-[200px]">
            <div className="min-h-0 flex-1">
              <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                <LineChart data={chartData} margin={{ top: 16, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10 }}
                    stroke="#94a3b8"
                    minTickGap={28}
                  />
                  <YAxis
                    type="number"
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    tick={{ fontSize: 10 }}
                    stroke="#94a3b8"
                    tickFormatter={(value) => `${value}%`}
                    width={44}
                    allowDataOverflow
                  />
                  <Tooltip
                    content={<HistoryTooltip />}
                    isAnimationActive={false}
                    wrapperStyle={{ zIndex: 20, outline: 'none' }}
                    cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }}
                  />
                  {visibleSeries.map((item) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.key}
                      stroke={item.color}
                      strokeWidth={2}
                      dot={{ r: 3.5, fill: item.color, strokeWidth: 0 }}
                      activeDot={{ r: 5 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="flex flex-wrap gap-1.5 border-t border-slate-100 px-2 pt-2 dark:border-slate-800">
              {seriesMeta.map((item) => {
                const on = enabledKeys.has(item.key);
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => toggleSeries(item.key)}
                    title={on ? 'Скрыть' : 'Показать'}
                    className={`inline-flex min-h-9 items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-medium transition sm:min-h-0 sm:px-1.5 sm:py-0.5 ${
                      on
                        ? 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
                        : 'border-transparent bg-slate-100 text-slate-400 line-through opacity-60 dark:bg-slate-800'
                    }`}
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: on ? item.color : '#94a3b8' }}
                    />
                    <span style={on ? { color: item.color } : undefined}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
